import { useMemo, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import NoDataImage from "assets/images/no-data2.svg";
import MedicalScribeFeedbackButtons from "features/medical-scribe/components/medical-scribe-feedback-buttons.component";
import { feedbackTranscriptType } from "features/medical-scribe/constants";
import {
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  getScribeModalPopoverZIndex
} from "features/medical-scribe/medical-scribe-constants";
import { getMedicalScribeText } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import {
  estimateTranscriptionTime,
  formatSeconds,
  hhmmssToSeconds
} from "features/medical-scribe/utils";
import type { DeepgramInput } from "services/types/ambient-scribe.type";

type TranscriptTabProps = {
  id: string | number;
  duration?: string | null;
  transcription?: DeepgramInput;
  status?: number;
  /** Combined truth: localGenerating.transcript (parent) */
  isGeneratingTranscription: boolean;
  /** Orchestrator handles mutation + paywall check + localGenerating state */
  onTranscribe: () => void;
};

export default function TranscriptTab({
  id,
  duration,
  transcription,
  status,
  isGeneratingTranscription,
  onTranscribe
}: TranscriptTabProps) {
  const theme = useTheme();
  const tooltipPopperProps = useMemo(
    () => ({
      style: { zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar) }
    }),
    [theme.zIndex.snackbar]
  );
  const [showCopied, setShowCopied] = useState(false);

  const handleCopyText = async () => {
    try {
      const text = transcription?.channels[0].alternatives[0]?.transcript ?? "";
      await navigator.clipboard.writeText(text);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const renderedWords = useMemo(() => {
    const transcript = getMedicalScribeText(
      transcription ? { transcription, patient: {} } : null
    );
    return (
      <Typography variant="body2" lineHeight={1.6}>
        {transcript}
      </Typography>
    );
  }, [transcription]);

  if (transcription) {
    return (
      <>
        <Stack
          mt={2}
          width="100%"
          direction="row"
          justifyContent="space-between"
        >
          <Tooltip
            title="Copy text"
            arrow
            disableInteractive
            PopperProps={tooltipPopperProps}
          >
            <IconButton
              onClick={handleCopyText}
              color="primary"
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {showCopied && (
            <Typography variant="caption" color="success.main" pl={2}>
              Copied!
            </Typography>
          )}
          <MedicalScribeFeedbackButtons
            scribeId={Number(id)}
            feedbackType={feedbackTranscriptType}
          />
        </Stack>
        {renderedWords}
      </>
    );
  }

  // No transcription yet — show generating placeholder or empty state
  const seconds = estimateTranscriptionTime(
    hhmmssToSeconds(String(duration))
  ).estimatedDurationSec;
  const formattedDuration = formatSeconds(seconds);

  if (isGeneratingTranscription) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Generation in progress…
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This may take a moment. The page will update automatically.
        </Typography>
      </Stack>
    );
  }

  if (status === AMBIENT_SCRIBE_STATUS_PROCESSING) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <CircularProgress size={32} />
        <Typography variant="body3" color="text.secondary">
          Processing your audio…
        </Typography>
        <Typography variant="body1" color="text.secondary">
          AI is analyzing the recording. Transcript will appear here
          automatically once complete.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      columnGap={2.5}
      alignItems="center"
      justifyContent="center"
      width="100%"
      minHeight="280px"
      mt={2}
    >
      <Box
        component="img"
        src={NoDataImage}
        alt=""
        sx={{ height: "130px", p: 2 }}
      />
      <Stack rowGap={3}>
        <Typography variant="body3">
          No transcription available yet. Click the button to generate.
        </Typography>
        {Boolean(duration) && (
          <Typography variant="body2" sx={{ fontStyle: "italic", mt: -2 }}>
            This may take up to ~{" "}
            {!duration || Number(seconds) < 60 ? "1 minute" : formattedDuration}
          </Typography>
        )}
        <Typography variant="body2" mt={-1} mb={1}>
          When it's done, you'll be able to generate Summary and Scribe Notes.
        </Typography>
        <LoadingButton
          variant="contained"
          disabled={!id}
          onClick={onTranscribe}
          loading={isGeneratingTranscription}
        >
          Transcribe Audio
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
